import {
  UserInfo,
  PortfolioProps,
  HomepagePortfolioInfoType,
  Structure,
} from "admin/type/type";
import { ApiStartup, ApiMember, ApiTeam, ApiStage } from "lib/latticeApi";

// ============================================================
// 이미지 오브젝트 (Supabase Storage)
// ============================================================

export interface ImageObject {
  object_url: string;
  name: string;
  size: number;
  type: string;
  signed_url?: string;
  path?: string;
  created_at?: string;
  updated_at?: string;
}

// ============================================================
// UI에서 사용하는 타입 별칭 (API 타입 기반)
// ============================================================

export type PublicTeam = ApiTeam;
export type PublicMember = ApiMember;
export type PublicStage = ApiStage;
export type PublicStartup = ApiStartup;
export type PublicPortfolio = ApiStartup;

// ============================================================
// 스토리지 URL 변환
// ============================================================

/** object_url(상대경로)을 퍼블릭 스토리지 URL로 변환 */
export function toPublicStorageUrl(objectUrl?: string): string | undefined {
  if (!objectUrl) return undefined;
  if (objectUrl.startsWith("http") || objectUrl.startsWith("blob:"))
    return objectUrl;
  const supabaseUrl = process.env.REACT_APP_LATTICE_SUPABASE_URL;
  if (!supabaseUrl) return undefined;
  return `${supabaseUrl}/storage/v1/object/public/public-resources/${objectUrl}`;
}

// ============================================================
// API → 기존 UI 타입 어댑터
// ============================================================

/** ApiTeam → Structure */
export function toStructure(team: ApiTeam): Structure {
  return {
    hpStructureId: team.id,
    name: team.name,
    introduction: team.description,
    parent: 0,
    structureIndex: 0,
  };
}

/** ApiMember → UserInfo */
export function toUserInfo(
  member: ApiMember,
  portfolios?: PortfolioProps[]
): UserInfo {
  const profileUrl = toPublicStorageUrl(member.profile_image?.[0]?.object_url);
  return {
    hpUserId: member.id,
    name: member.name,
    email: member.email,
    profileImgUrl: profileUrl,
    introduction: member.introduction,
    keyword: member.keywords ?? null,
    hpStructure: member.team_id ? { hpStructureId: member.team_id } : undefined,
    hpPosition: member.position_name
      ? { hpPositionId: 0, name: member.position_name }
      : undefined,
    twitter: member.sns_links?.twitter,
    facebook: member.sns_links?.facebook,
    blog: member.sns_links?.blog,
    instagram: member.sns_links?.instagram,
    linkedin: member.sns_links?.linkedin,
    website: member.sns_links?.website,
    careerAndEducation: {
      education: member.education?.map((e) => ({
        schoolName: e.school,
        major: e.major,
      })),
      career: member.career?.map((c) => ({
        company: c.company,
        position: c.position,
      })),
    },
    hpUserMajor: member.keywords?.map((name, idx) => ({
      hpIndustrialTech: { acIndustrialTechId: idx, name },
    })),
    hpPortfolio: portfolios,
    acUserInfo:
      member.position_name ||
      member.organization_names?.length ||
      member.leader_role
        ? {
            acPosition: member.position_name
              ? { name: member.position_name }
              : undefined,
            acStructure: member.organization_names?.length
              ? { department: member.organization_names.join(" / ") }
              : undefined,
            acResponsibility: member.leader_role
              ? { name: member.leader_role }
              : undefined,
          }
        : undefined,
  };
}

/** ApiStartup → PortfolioProps (목록용) */
export function toPortfolioProps(startup: ApiStartup): PortfolioProps {
  const logoUrl =
    toPublicStorageUrl(startup.profile_image?.[0]?.object_url) ?? "";
  return {
    active: true,
    portfolioId: startup.id as unknown as number,
    companyName: startup.name,
    description: startup.description ?? "",
    companyImgKey: "",
    companyImgUrl: logoUrl,
    introduction: startup.introduction ?? "",
    stage: "",
    hpPfIndustrialTech: (startup.industry_names ?? []).map((name, idx) => ({
      hpIndustrialTech: { acIndustrialTechId: idx, name },
      hpPfIndustrialTechId: idx,
    })),
    homepageRegisterDate: "",
    preLatticePortfolioUpdatedAt: "",
    curLatticePortfolioUpdatedAt: "",
  };
}

/** ApiStartup → HomepagePortfolioInfoType (상세용) */
export function toHomepagePortfolioInfo(
  startup: ApiStartup
): HomepagePortfolioInfoType {
  const logoUrl =
    toPublicStorageUrl(startup.profile_image?.[0]?.object_url) ?? "";
  return {
    portfolioId: startup.id as unknown as number,
    companyName: startup.name,
    description: startup.description ?? "",
    introduction: startup.introduction,
    companyImgKey: null,
    companyImgUrl: logoUrl || null,
    ceoName: startup.ceo_name ?? "",
    establishmentDay: startup.establishment_date ?? "",
    homepageUrl: startup.homepage_url ?? null,
    addr1: startup.address?.country ?? "",
    addr2: "",
    addr3: "",
    addrDetail: startup.address?.address_line,
    stage: "",
    hpPfIndustrialTech: (startup.industry_names ?? []).map((name, idx) => ({
      hpIndustrialTech: { acIndustrialTechId: idx, name },
      hpPfIndustrialTechId: idx,
    })),
    hpPfProject: [],
  };
}
